import { z } from 'zod';

/**
 * Rollen der Mitarbeiter:innen – gemeinsam für Ticketshop und Website. Eine Person kann mehrere
 * Rollen haben. Käufer:innen im Shop haben keine dieser Rollen.
 */
export const STAFF_ROLES = ['admin', 'tickets', 'web'] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];

/** Cognito-Gruppe je Rolle im gemeinsamen User Pool. */
export const STAFF_ROLE_GROUP: Record<StaffRole, string> = {
  admin: 'admins',
  tickets: 'tickets',
  web: 'web',
};

export const STAFF_ROLE_LABEL: Record<StaffRole, string> = {
  admin: 'Admin',
  tickets: 'Ticket',
  web: 'Web',
};

export const STAFF_ROLE_DESCRIPTION: Record<StaffRole, string> = {
  admin: 'Alles, inklusive Benutzerverwaltung',
  tickets: 'Ticket-Verwaltung: Veranstaltungen, Bestellungen, Abendkasse, Einlass',
  web: 'Website-Verwaltung: Beiträge, Seiten, Einstellungen der Website',
};

export function isStaffRole(value: unknown): value is StaffRole {
  return typeof value === 'string' && (STAFF_ROLES as readonly string[]).includes(value);
}

/** Rollen aus den Cognito-Gruppen eines Tokens; unbekannte Gruppen werden ignoriert. */
export function rolesFromGroups(groups: readonly string[]): StaffRole[] {
  return STAFF_ROLES.filter((role) => groups.includes(STAFF_ROLE_GROUP[role]));
}

/** Rollen in fester Reihenfolge und ohne Doppelte. */
export function normalizeRoles(roles: readonly StaffRole[]): StaffRole[] {
  return STAFF_ROLES.filter((role) => roles.includes(role));
}

export function canManageTickets(roles: readonly string[]): boolean {
  return roles.includes('admin') || roles.includes('tickets');
}

export function canManageWebsite(roles: readonly string[]): boolean {
  return roles.includes('admin') || roles.includes('web');
}

export function canManageUsers(roles: readonly string[]): boolean {
  return roles.includes('admin');
}

export interface StaffUser {
  email: string;
  roles: StaffRole[];
  createdAt: string;
  /** Eingeladen, aber noch nie angemeldet (Startpasswort noch nicht geändert) */
  pending: boolean;
}

const rolesSchema = z
  .array(z.enum(STAFF_ROLES))
  .min(1, 'Mindestens eine Rolle wählen')
  .transform((roles) => normalizeRoles(roles));

export const staffUserInputSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email('Ungültige E-Mail-Adresse')),
  roles: rolesSchema,
});

export type StaffUserInput = z.infer<typeof staffUserInputSchema>;

export const staffRolesUpdateSchema = z.object({ roles: rolesSchema });

/**
 * Schutzregeln der Benutzerverwaltung. Liefert die Fehlermeldung oder `null`, wenn die Änderung
 * erlaubt ist. `nextRoles = null` heißt: Zugang löschen.
 */
export function staffChangeError(actor: string, users: readonly StaffUser[], target: string, nextRoles: readonly StaffRole[] | null): string | null {
  const losesAdmin = nextRoles === null || !nextRoles.includes('admin');
  if (target === actor && nextRoles === null) {
    return 'Sie können Ihren eigenen Zugang nicht löschen.';
  }
  if (target === actor && losesAdmin) {
    return 'Sie können sich die Admin-Rolle nicht selbst entziehen.';
  }
  const remainingAdmins = users.filter((user) => user.roles.includes('admin') && user.email !== target).length;
  const targetIsAdmin = users.some((user) => user.email === target && user.roles.includes('admin'));
  if (targetIsAdmin && losesAdmin && remainingAdmins === 0) {
    return 'Es muss immer mindestens einen Admin geben.';
  }
  return null;
}
